/**
 * Pager derivation for the results list.
 *
 * The two feeds behind the results surface report their paging differently:
 *
 * - The public archive feed (`/release`) returns `pageInfo.total`, so the page count is
 *   knowable and the list gets the full numbered pager.
 * - The member feed (`/messageReceipt/me`) deliberately skips the `COUNT(*)` (#2059) —
 *   on a graph-joined select it was two thirds of the request's database time and a
 *   large part of the release-time 503s. `skipTotal` fetches `limit + 1` rows instead,
 *   so `total` is undefined but `pageInfo.hasNextPage` is exact.
 *
 * Deriving the pager from `total` alone collapsed the member page count to 1 and hid
 * the controls entirely, stranding a member on their most recent page (#2084). When the
 * total is unknown, `hasNextPage` (plus the current page number) is enough for a
 * Previous/Next pair, so the rest of the record stays reachable without restoring the
 * count.
 */
export type PagerMode = 'none' | 'numbered' | 'prevNext';

export type Pager = {
  /** 'numbered' when the total is known, 'prevNext' when only hasNextPage is, else 'none'. */
  mode: PagerMode;
  /** Total pages; only meaningful (and only rendered) in 'numbered' mode. */
  pageCount: number;
  /** Page the Previous control points at (clamped to 1). */
  previousPage: number;
  /** Page the Next control points at (the current page when there is nothing after it). */
  nextPage: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

export function derivePager({
  page,
  perPage,
  total,
  hasNextPage,
}: {
  page: number;
  perPage: number;
  total?: number;
  hasNextPage?: boolean;
}): Pager {
  const current = Math.max(1, Math.floor(page) || 1);
  const knowsTotal = typeof total === 'number' && perPage > 0;
  const pageCount = knowsTotal ? Math.ceil(total / perPage) || 1 : 1;

  // Previous is driven by the page number alone, never by the feed: the last page must
  // still offer a way back even though it reports no next page.
  const hasPrevious = current > 1;
  const hasNext = knowsTotal ? current < pageCount : !!hasNextPage;

  const mode: PagerMode = knowsTotal
    ? pageCount > 1
      ? 'numbered'
      : 'none'
    : hasNext || hasPrevious
      ? 'prevNext'
      : 'none';

  return {
    mode,
    pageCount,
    previousPage: Math.max(current - 1, 1),
    // Disabled controls are styled, not inert, so a dead Next has to point at the page
    // the reader is already on rather than off the end of the list.
    nextPage: knowsTotal ? Math.min(current + 1, pageCount) : hasNext ? current + 1 : current,
    hasPrevious,
    hasNext,
  };
}
